import React, {useEffect, useMemo, useState} from 'react';
import {
  Input,
  FormLabel,
  FormControl,
  Modal,
  ModalBody,
  ModalOverlay,
  Box,
  Flex,
  Spinner,
  FormHelperText,
  Button,
  ModalContent,
  ModalCloseButton,
} from '@chakra-ui/react';
import {Controller, useForm} from 'react-hook-form';
import {useGetTestLists} from '../../hooks/api/testList';
import {
  CampaignChatQueryKeys,
  useGenerateCampaigns,
} from '../../hooks/api/chatabm';
import {ObjectId} from 'bson';
import {useTask} from '../../hooks/api/tasks';
import {MinCampaignsTestListSize} from '../../shared/chatabm';
import {WizardContent} from '../Agents/AgentBuilder/wizard/WizardContent';
import {isGenerateCampaignsState} from '../../shared/tasks/state';
import Select from 'react-select';
import {useNavigate} from 'react-router';
import {useQueryClient} from '@tanstack/react-query';

export const CreateCampaignChatModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isCentered={true} isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <CreateCampaignChatModalContent onClose={onClose} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const CreateCampaignChatModalContent = ({onClose}: {onClose: () => void}) => {
  const testLists = useGetTestLists({});
  const generateCampaigns = useGenerateCampaigns();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [taskId, setTaskId] = useState<ObjectId | null>(null);
  const {isTaskInProgress, taskQuery} = useTask(
    {type: 'generateCampaigns'},
    {
      onTaskComplete: (task) => {
        setTaskId(task.id);
      },
    }
  );

  const testListOptions = useMemo(
    () =>
      (testLists.data ?? []).map((testList) => ({
        label: testList.label,
        value: testList._id,
        isDisabled:
          testList.status.type === 'processing' ||
          testList.status.numAccounts < MinCampaignsTestListSize,
      })),
    [testLists.data]
  );

  const {
    control,
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<{
    campaignName: string;
    domain: string;
    testList: {
      label: string;
      value: ObjectId;
    };
  }>();

  const onGenerateCampaigns = handleSubmit((data) => {
    const {campaignName, domain, testList} = data;
    if (!testList || !domain || !campaignName) {
      return;
    }

    generateCampaigns.mutate({
      campaignName,
      domain,
      testListId: testList.value,
    });
  });

  useEffect(() => {
    if (!taskId) {
      return;
    }

    const state = taskQuery.data?.detailedState;
    if (isGenerateCampaignsState(state)) {
      queryClient.invalidateQueries({queryKey: CampaignChatQueryKeys.list()});
      navigate(`/chats/${state.campaignChatId.toString()}`);
      onClose();
    }
  }, [taskId, taskQuery.data?.detailedState, navigate, queryClient, onClose]);

  if (isTaskInProgress || generateCampaigns.isPending) {
    return (
      <WizardContent
        title="Your campaigns are being generated..."
        footer="This usually takes ~20-35 seconds"
        bgColor="kgray.50"
      >
        <Spinner size="xl" color="kred.300" />
      </WizardContent>
    );
  }

  return (
    <Box as="form" onSubmit={onGenerateCampaigns} py="4" pt="10">
      <Flex direction="column" gap="10">
        <FormControl isInvalid={!!errors.campaignName}>
          <FormLabel>Campaign Name</FormLabel>
          <Input
            isDisabled={generateCampaigns.isPending}
            {...register('campaignName', {required: true})}
          />
        </FormControl>

        <FormControl isInvalid={!!errors.testList}>
          <FormLabel>
            Select a customer list to discover your high-value campaigns
          </FormLabel>
          <Controller
            name={'testList'}
            control={control}
            rules={{required: true}}
            render={({field, fieldState: {error}}) => (
              <Select
                {...field}
                isLoading={testLists.isPending || generateCampaigns.isPending}
                options={testListOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    // Outline the component in red when there is an error
                    borderColor: error ? 'red' : base.borderColor,
                    boxShadow: error ? '0 0 0 1px red' : base.boxShadow,
                    '&:hover': {
                      borderColor: error ? 'red' : base.borderColor,
                    },
                  }),
                }}
              />
            )}
          />
          <FormHelperText>
            Minimum test list size is {MinCampaignsTestListSize}.
          </FormHelperText>
        </FormControl>

        <FormControl isInvalid={!!errors.domain}>
          <FormLabel>Enter your website URL for context</FormLabel>
          <Input
            autoComplete="off"
            isDisabled={generateCampaigns.isPending}
            placeholder="example.com"
            {...register('domain', {
              required: true,
            })}
          />
        </FormControl>

        <Button
          alignSelf="flex-end"
          colorScheme="kbuttonblue"
          isLoading={generateCampaigns.isPending}
          type="submit"
          w="fit-content"
        >
          Generate Campaigns
        </Button>
      </Flex>
    </Box>
  );
};
